import {
  Box,
  Typography,
  Paper,
  IconButton,
  Collapse,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  Badge,
  TextField,
  CircularProgress,
  Link,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { MoreVert, PictureAsPdf, ExitToApp } from "@mui/icons-material";
import { backendGet, backendPost } from "@accredo/fe-shared/services/dataService.js";
import { getDatumSortable } from "@accredo/fe-shared/services/formatService.js";
import { useSnackbar } from "notistack";
import { Link as RouterLink, useNavigate } from "react-router";
import { useConfirm } from "@accredo/fe-shared/contexts/ConfirmContext.jsx";
import ClaimStatusChanger from "./ClaimStatusChanger.jsx";
import ClaimStatusBadge from "./ClaimStatusBadge.jsx";
import CoverageSplitDialog from "./modals/CoverageSplitDialog.jsx";
import {
  History as HistoryIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { getEnv } from "@accredo/fe-shared/env.js";
import ClaimStatusHistoryModal from "./modals/ClaimStatusHistoryModal.jsx";
import { Button as DsButton } from "@accredo/fe-shared/theme/components";
import EditButton from "@accredo/fe-shared/theme/components/EditButton.jsx";

import { getDatum, getDatumZeit, getEuro } from "@accredo/fe-shared/services/formatService.js";
import { useClaimPermissions } from "../hooks/permissions/useClaimPermissions.jsx";
import ClaimCostsModal from "./modals/ClaimCostsModal.jsx";
import { useClaimActions } from "../hooks/useClaimActions.jsx";
import { COVERAGE_TYPES_FIELD } from "./Form/FormDefinitionRenderer/coverageSelection.js";

/**
 * ClaimInfo Component - Main claim information display
 * Based on Figma design: https://www.figma.com/design/Bmp4tsBBAS8pI9OaFyWewR/accredo-Schadenportal-Layout?node-id=9248-13890
 *
 * @param {Object} claim - The claim data object
 * @param {Function} onEdit - Callback for edit button (hidden in readOnly mode)
 * @param {boolean} readOnly - If true, hides all editing/action UI elements
 * @param {Object} formDefinition - Definition of the claim's form, for the Sparten dialog of "Freigeben"
 */
export default function ClaimInfo({
  claim,
  onEdit,
  readOnly = false,
  showContent,
  setShowContent,
  onRefresh,
  formDefinition,
}) {
  const schadenData = claim;
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { canUpdate, canExportZip } = useClaimPermissions();

  const [kostenpositionenExpanded, setKostenpositionenExpanded] =
    useState(false);

  // const [showContent, setShowContent] = useState(true);
  const [isManuallyToggled, setIsManuallyToggled] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false); // Modal-Status

  // Costs editor modal state
  const [isCostsModalOpen, setIsCostsModalOpen] = useState(false);

  // Claim note state
  const [noteValue, setNoteValue] = useState("");
  const [isNoteEditing, setIsNoteEditing] = useState(false);
  const [isNoteSaving, setIsNoteSaving] = useState(false);
  const noteInputRef = useRef(null);
  const { updateClaimFormdata, updateClaimStatus, findById } =
    useClaimActions();
  const confirm = useConfirm();
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    // Skip scroll behavior in readOnly mode
    if (readOnly || isManuallyToggled) return;
    const handleScroll = () => {
      const shouldShow = window.scrollY < 100;
      if (showContent !== shouldShow) {
        setShowContent(shouldShow);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [readOnly, isManuallyToggled]);

  // Sync note value from formdata when claim changes
  useEffect(() => {
    if (!claim) return;
    const parsed = JSON.parse(schadenData.formdata || "{}");
    setNoteValue(parsed.claim_note || "");
  }, [schadenData.id, schadenData.formdata]);

  if (!claim) {
    return null;
  }

  // Parse formdata
  const formdata = JSON.parse(schadenData.formdata || "{}");

  // Prepare claim data for ClaimInfo component
  const claimInfoData = {
    id: schadenData.id,
    title: `Inhalt ${schadenData.objekt?.bezeichnung || ""}`,
    subtitle: schadenData.claim_form_label || schadenData.sparte || "",
    last_transition_failed: schadenData.last_transition_failed || false,
    status: schadenData.status?.value || "unknown",
    statusLabel: schadenData.status?.label || "Unbekannt",
    versicherungsnehmer: schadenData.kunde?.display_name || "",
    versicherer: schadenData.vertrag?.gesellschaft?.name || "",
    versicherungsnummer: schadenData.vertrag?.vertragsnummer || "",
    object_ref_num_label: schadenData.kunde?.object_ref_num_label || "",
    wirtschaftseinheit: schadenData.objekt?.customer_reference_number || "",
    vorgangsnummer_kunde: schadenData.schaden_nummer_kd || "",
    schaden_nummer_accredo: schadenData.schaden_nummer_accredo || "",
    schaden_nummer_vu: schadenData.schaden_nummer_vu || "",
    schadendatum: getDatum(schadenData.schaden_datum),
    erstellt: getDatumZeit(schadenData.created_at),
    zuletzt_aktualisiert: getDatumZeit(schadenData.updated_at),
    kostenvoranschlag: getEuro(formdata.schaden_kostenvoranschlag),
    rechnungsbetrag: getEuro(formdata.schaden_rechnungsbetrag),
    costs: schadenData?.costs || [],
    gesamtschadenkosten: getEuro(formdata.gesamtschadenkosten),
    selbstbehalt: schadenData.sb_betrag ? getEuro(schadenData.sb_betrag) : null,
    sb_label: schadenData.sb_label || "Selbstbeteiligung",
    auszahlungsbetrag: getEuro(schadenData.auszahlungsbetrag),
  };

  // Beim manuellen Toggle:
  const handleManualToggle = (value) => {
    setShowContent(value);
    setIsManuallyToggled(true);
  };

  const regeneratePdf = async () => {
    const damage_id = claim.id;
    await backendPost("data/schadens/regenerate-pdf/" + damage_id, {
      label: "PDF",
      setIsLoading,
      toSend: {},
      setResult: () => {
        // refresh route per navigate
        navigate(0);
      },
      enqueueSnackbar,
    });
  };

  const canRegeneratePdf = claim?.permissions?.can_regenerate_pdf === true;
  const canApprove = claim?.permissions?.can_approve === true && !readOnly;
  const splitSiblings = claim?.permissions?.split_siblings ?? [];
  const isPendingSplit =
    !claim?.split_group_id &&
    (formdata[COVERAGE_TYPES_FIELD]?.length ?? 0) > 1;

  const approve = async () => {
    if (isPendingSplit) {
      setIsSplitDialogOpen(true);
      return;
    }
    const confirmed = await confirm({
      title: "Freigeben",
      message: "Schaden freigeben und zur Prüfung melden?",
      confirmLabel: "Freigeben",
    });
    if (confirmed) {
      await updateClaimStatus(claim.id, "pre_check", {
        label: "Schaden freigeben",
        setIsLoading,
      });
    }
  };

  const handleSplit = async () => {
    setIsSplitDialogOpen(false);
    await findById(claim.id);
    navigate(`/schaeden/${claim.id}?tab=schadenformular&dialog=pre_check`);
  };

  const doExport = () => {
    backendGet("data/schaden/export/" + claim.id, {
      label: "Export",
      setIsLoading: setIsLoading,
      setResult: download,
      enqueueSnackbar: enqueueSnackbar,
      options: { responseType: "blob" },
    });
  };

  const download = (response) => {
    const blob = new Blob([response], { type: "application/zip" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download =
      "Schaden_" +
      getDatumSortable(new Date()) +
      "_" +
      claim.schaden_nummer_accredo +
      ".zip";

    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Costs editor modal handlers
  const handleOpenCostsModal = () => {
    setIsCostsModalOpen(true);
  };

  const handleCloseCostsModal = () => {
    setIsCostsModalOpen(false);
  };

  return (
    <>
      <Paper
        sx={{
          backgroundColor: "background.subtle",
          boxShadow: 0,
          borderRadius: "10px",
          p: 3,
          position: "relative",
          ...(readOnly
            ? {}
            : {
                position: "sticky",
                top: 72,
                zIndex: 3,
                mb: 2,
              }),
        }}
      >
        {/* Header Section */}
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ color: "text.default" }}>
                {claimInfoData.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {claimInfoData.subtitle}
              </Typography>
            </Box>
            {!readOnly && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "end",
                  gap: 2,
                  flexWrap: "wrap",
                  marginLeft: "auto",
                }}
              >
                <Box>
                  <ClaimStatusChanger key={claim.status?.value} claim={claim} />
                </Box>

                {canApprove && (
                  <DsButton
                    variant="primary"
                    onClick={approve}
                    disabled={
                      isPendingSplit && !formDefinition?.coverage_types?.length
                    }
                  >
                    Freigeben
                  </DsButton>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "end",
                    gap: 2,
                    marginLeft: "auto",
                  }}
                >
                  {(canExportZip() || canRegeneratePdf) && (
                    <Box>
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e)}
                        >
                          <MoreVert />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={handleMenuClose}
                        >
                          {canRegeneratePdf && (
                            <MenuItem
                              onClick={() => {
                                handleMenuClose();
                                regeneratePdf();
                              }}
                              // disabled={booking.status !== "pending"}
                            >
                              <PictureAsPdf
                                style={{
                                  marginRight: "8px",
                                }}
                              />
                              Pdf generieren
                            </MenuItem>
                          )}
                          {canExportZip() && (
                            <MenuItem
                              onClick={() => {
                                handleMenuClose();
                                doExport();
                              }}
                            >
                              <ExitToApp
                                style={{
                                  marginRight: "8px",
                                }}
                              />
                              Exportieren
                            </MenuItem>
                          )}
                        </Menu>
                      </>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
          {splitSiblings.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Weitere Schäden zum selben Ereignis
              </Typography>
              {splitSiblings.map((sibling) => (
                <Box
                  key={sibling.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <Link
                    component={RouterLink}
                    to={`/schaeden/${sibling.id}`}
                    variant="body2"
                  >
                    {sibling.schaden_nummer_accredo} · {sibling.sparte}
                  </Link>
                  <ClaimStatusBadge
                    status={sibling.status?.value}
                    label={sibling.status?.label}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Main Content Grid */}
        <Collapse in={showContent} timeout={800} unmountOnExit>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              gap: 3,
              mt: 3,
            }}
          >
            {/* Left Column - Schadendaten */}
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, color: "text.default" }}
                >
                  Schadendaten
                </Typography>
                {canUpdate(claim) && onEdit && !readOnly && (
                  <EditButton onClick={onEdit}>Bearbeiten</EditButton>
                )}
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    md: "1fr",
                    lg: "minmax(0, 180px) 1fr",
                  },
                  columnGap: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.secondary", mb: 0.25 }}
                >
                  Versicherungsnehmer
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1.5 }}
                >
                  {claimInfoData.versicherungsnehmer || "–"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.secondary", mb: 0.25 }}
                >
                  Versicherer
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1.5 }}
                >
                  {claimInfoData.versicherer || "–"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.secondary", mb: 0.25 }}
                >
                  Versicherungsnummer
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1.5 }}
                >
                  {claimInfoData.versicherungsnummer || "–"}
                </Typography>

                {claimInfoData.object_ref_num_label && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mb: 0.25,
                      }}
                    >
                      {claimInfoData.object_ref_num_label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mb: 1.5 }}
                    >
                      {claimInfoData.wirtschaftseinheit || "–"}
                    </Typography>
                  </>
                )}

                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.secondary", mb: 0.25 }}
                >
                  {"Vorgangsnummer"}
                  <br />
                  {"(Kunde)"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1.5 }}
                >
                  {claimInfoData.vorgangsnummer_kunde || "–"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.secondary", mb: 0.25 }}
                >
                  {"Schadennummer"}
                  <br />
                  {`(${getEnv("VITE_TENANT_LABEL_SHORT") || "accredo"})`}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1.5 }}
                >
                  {claimInfoData.schaden_nummer_accredo || "–"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.secondary", mb: 0.25 }}
                >
                  Schadennummer (VR)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1.5 }}
                >
                  {claimInfoData.schaden_nummer_vu || "–"}
                </Typography>
              </Box>
            </Box>

            {/* Middle Column - Dates and Status History */}
            <Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <InfoItem
                  label="Schadendatum"
                  value={claimInfoData.schadendatum}
                  vertical
                />
                <InfoItem
                  label="Erstellt"
                  value={claimInfoData.erstellt}
                  vertical
                />
                <InfoItem
                  label="Zuletzt aktualisiert"
                  value={claimInfoData.zuletzt_aktualisiert}
                  vertical
                />
                {!readOnly && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "text.subtle" }}
                    >
                      Status-Historie
                      <Tooltip
                        title={
                          claimInfoData.last_transition_failed
                            ? "Der letzte Statuswechsel ist fehlgeschlagen und wurde zurückgesetzt"
                            : "Status-Verlauf anzeigen"
                        }
                      >
                        <IconButton onClick={() => setIsHistoryModalOpen(true)}>
                          <Badge
                            color="error"
                            badgeContent={<WarningAmberIcon sx={{ fontSize: 12 }} />}
                            invisible={!claimInfoData.last_transition_failed}
                          >
                            <HistoryIcon />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.subtle", mb: 0.5 }}
                >
                  Ansprechpartner
                </Typography>
                {claim?.vertrag?.sachbearbeiter ? (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {claim.vertrag.sachbearbeiter.vorname}{" "}
                      {claim.vertrag.sachbearbeiter.nachname}
                    </Typography>
                    {claim.vertrag.sachbearbeiter.telefon && (
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        Telefon: {claim.vertrag.sachbearbeiter.telefon}
                      </Typography>
                    )}
                    {claim.vertrag.sachbearbeiter.fax && (
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        Telefax: {claim.vertrag.sachbearbeiter.fax}
                      </Typography>
                    )}
                    {claim.vertrag.sachbearbeiter.email && (
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        E-Mail: {claim.vertrag.sachbearbeiter.email}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: "text.disabled", ml: 1 }}
                  >
                    Kein Ansprechpartner hinterlegt
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Right Column - Kosten */}
            <Box>
              <Paper
                sx={{
                  backgroundColor: "background.paper",
                  borderRadius: "10px",
                  p: 2,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <CostItem
                    label="Kostenvoranschlag"
                    value={claimInfoData.kostenvoranschlag}
                  />
                  <CostItem
                    label="Rechnungsbetrag"
                    value={claimInfoData.rechnungsbetrag}
                  />

                  <Box
                    sx={{
                      height: 0,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      my: 0.5,
                    }}
                  />

                  <CostItem
                    label="Kostenpositionen"
                    value={claimInfoData.gesamtschadenkosten}
                    expandable={claimInfoData?.costs?.length > 0}
                    expanded={kostenpositionenExpanded}
                    onClick={() =>
                      setKostenpositionenExpanded(!kostenpositionenExpanded)
                    }
                  />

                  <Collapse in={kostenpositionenExpanded}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      {claimInfoData.costs?.map((item, index) => (
                        <CostItem
                          key={index}
                          label={
                            item.data?.cost_label ||
                            item?.cost_type_label ||
                            item.cost_type
                          }
                          value={item.amount}
                        />
                      ))}
                    </Box>
                  </Collapse>

                  {/* Edit costs button */}
                  {canUpdate(claim) && !readOnly && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                      }}
                    >
                      <EditButton onClick={handleOpenCostsModal}>
                        Bearbeiten
                      </EditButton>
                    </Box>
                  )}

                  <Box
                    sx={{
                      height: 0,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      my: 0.5,
                    }}
                  />

                  <CostItem
                    label={claimInfoData.sb_label}
                    value={claimInfoData.selbstbehalt}
                  />
                  <CostItem
                    label="Auszahlungsbetrag"
                    value={claimInfoData.auszahlungsbetrag}
                  />
                </Box>
              </Paper>

              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "text.subtle" }}
                  >
                    Zusatzinfos
                  </Typography>
                  {canUpdate(claim) && !readOnly && !isNoteEditing && (
                    <EditButton
                      onClick={() => {
                        setIsNoteEditing(true);
                        setTimeout(() => noteInputRef.current?.focus(), 0);
                      }}
                    >
                      Bearbeiten
                    </EditButton>
                  )}
                </Box>
                {isNoteEditing ? (
                  <Box>
                    <TextField
                      inputRef={noteInputRef}
                      multiline
                      minRows={3}
                      maxRows={8}
                      fullWidth
                      size="small"
                      value={noteValue}
                      onChange={(e) => setNoteValue(e.target.value)}
                      disabled={isNoteSaving}
                      placeholder="Zusätzliche Informationen zum Schaden"
                      sx={{ mb: 1 }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        size="small"
                        color="inherit"
                        variant="outlined"
                        onClick={() => {
                          setNoteValue(formdata.claim_note || "");
                          setIsNoteEditing(false);
                        }}
                        disabled={isNoteSaving}
                        sx={{
                          borderColor: "divider",
                          color: "text.secondary",
                          fontSize: 12,
                          padding: "2px 8px",
                          fontWeight: 400,
                        }}
                      >
                        Abbrechen
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={async () => {
                          setIsNoteSaving(true);
                          await updateClaimFormdata(
                            claim.id,
                            { claim_note: noteValue },
                            {
                              setIsLoading: setIsNoteSaving,
                              setResult: () => {
                                setIsNoteEditing(false);
                              },
                            }
                          );
                          setIsNoteSaving(false);
                        }}
                        disabled={isNoteSaving}
                        endIcon={
                          isNoteSaving ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : null
                        }
                        sx={{
                          fontSize: 12,
                          padding: "2px 8px",
                          fontWeight: 400,
                        }}
                      >
                        Speichern
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: noteValue ? "text.secondary" : "text.disabled",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {noteValue || "Noch nichts hinterlegt"}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Collapse>

        {setShowContent && (
          <Box
            sx={{
              display: "flex",
              marginBottom: -5,
              alignItems: "center",
              justifyContent: "center",
            }}
            role="button"
            aria-label="Schadeninfo ein-/ausblenden"
            aria-expanded={showContent}
            onClick={() => handleManualToggle(!showContent)}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                color: "text.secondary",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  cursor: "pointer",
                  "&:hover": { color: "primary.main" },
                  backgroundColor: "background.subtle",
                  width: "42px",
                  height: "42px",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  transition: "transform 0.3s",
                  transform: showContent ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                <ExpandMoreIcon fontSize="medium" />
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      <ClaimStatusHistoryModal
        open={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        claimId={claimInfoData.id}
      />

      {/* Costs Editor Modal */}
      <ClaimCostsModal
        claimId={claim.id}
        costs={schadenData?.costs || []}
        coverageType={schadenData?.sparte_coverage_type ?? null}
        costTypes={schadenData?.claim_cost_types ?? null}
        open={isCostsModalOpen}
        onClose={handleCloseCostsModal}
        onSuccess={onRefresh}
      />

      {canApprove && (
        <CoverageSplitDialog
          open={isSplitDialogOpen}
          claim={claim}
          formDefinition={formDefinition}
          onClose={() => setIsSplitDialogOpen(false)}
          onSplit={handleSplit}
        />
      )}
    </>
  );
}

ClaimInfo.propTypes = {
  claim: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    status: PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
    versicherungsnehmer: PropTypes.string,
    versicherer: PropTypes.string,
    versicherungsnummer: PropTypes.string,
    vorgangsnummer_kunde: PropTypes.string,
    schaden_nummer_accredo: PropTypes.string,
    schaden_nummer_vu: PropTypes.string,
    schadendatum: PropTypes.string,
    erstellt: PropTypes.string,
    zuletzt_aktualisiert: PropTypes.string,
    kostenvoranschlag: PropTypes.string,
    rechnungsbetrag: PropTypes.string,
    kostenpositionen_total: PropTypes.string,
    kostenpositionen: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    wiederherstellungskosten: PropTypes.string,
    mietminderung: PropTypes.string,
    mietausfall: PropTypes.string,
    ersatzunterkunft: PropTypes.string,
    stromkosten: PropTypes.string,
    gesamtschadenkosten: PropTypes.string,
    selbstbehalt: PropTypes.string,
    auszahlungsbetrag: PropTypes.string,
  }),
  onEdit: PropTypes.func,
  showContent: PropTypes.bool,
  readOnly: PropTypes.bool,
  onRefresh: PropTypes.func,
};

const InfoItem = ({ label, value, vertical = false }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: vertical ? "column" : "row",
      gap: vertical ? 0 : 1.5,
      alignItems: vertical ? "flex-start" : "center",
    }}
  >
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: "text.secondary",
        minWidth: vertical ? "auto" : "160px",
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: "text.secondary",
      }}
    >
      {value || "–"}
    </Typography>
  </Box>
);

InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.node,
  ]),
  vertical: PropTypes.bool,
};

const CostItem = ({
  label,
  value,
  onClick,
  expandable = false,
  expanded = false,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      cursor: expandable ? "pointer" : "default",
    }}
    onClick={onClick}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      {expandable && (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Box>
      )}
    </Box>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: "text.secondary" }}
    >
      {value || "–"}
    </Typography>
  </Box>
);

CostItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClick: PropTypes.func,
  expandable: PropTypes.bool,
  expanded: PropTypes.bool,
};
